import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, Grid, Switch, FormControlLabel,
    LinearProgress, Breadcrumbs, Link, Stack, FormHelperText} from '@mui/material';
import { Home, InsertPhoto } from '@mui/icons-material';
import sliderApi from '../../../../services/sliderApi';
import ImageUploader from '../../common/ImageUploader';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';
import { getImageUrl } from '../../../../services/api';

const SliderImageBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        alt: '',
        isActive: true,
        image: null
    });
    const [previewUrl, setPreviewUrl] = useState('');
    const [existingImageUrl, setExistingImageUrl] = useState('');
    const [errors, setErrors] = useState({ title: '', alt: '', image: '' });
    const [touched, setTouched] = useState({ title: false, alt: false, image: false });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isNew) {
            const fetchSlider = async () => {
                try {
                    const slider = await sliderApi.getById(id);
                    setFormData({
                        title: slider.title || '',
                        subtitle: slider.subtitle || '',
                        alt: slider.altText || '',
                        isActive: slider.isActive
                    });
    
                    if (slider.imageUrl) {
                        const fullUrl = getImageUrl(slider.imageUrl);
                        setPreviewUrl(fullUrl);
                        setExistingImageUrl(fullUrl);
                    }
                } catch (error) {
                    console.error('Fetch error:', error);
                    Swal.fire('Error', 'Failed to load slider', 'error');
                    navigate('/admin/sliders');
                } finally {
                    setLoading(false);
                }
            };
            fetchSlider();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const validateField = (name, value) => {
        switch (name) {
            case 'title': return value.trim() ? '' : 'Title is required';
            case 'alt': return value.trim() ? '' : 'Alt text is required';
            case 'image': 
                return (isNew ? !previewUrl : (!previewUrl && !existingImageUrl)) 
                    ? 'Image is required' 
                    : '';
            default: return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };

    const handleImageSelect = (file) => {
        if (!file) return;
        
        if (previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        const objectUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, image: file }));
        setPreviewUrl(objectUrl);
        setErrors(prev => ({ ...prev, image: '' }));
    };

    const handleSubmit = async () => {
        setTouched({ title: true, alt: true, image: true });
        if (!formData.title.trim() || !formData.alt.trim()) {
          setErrors({
            title: !formData.title.trim() ? 'Title is required' : '',
            alt: !formData.alt.trim() ? 'Alt text is required' : '',
            image: isNew && !previewUrl ? 'Image is required' : ''
          });
          return;
        }
      
        if (isNew && !previewUrl) {
          setErrors(prev => ({ ...prev, image: 'Image is required' }));
          return;
        }
      
        const confirm = await Swal.fire({
          title: 'Confirm',
          text: `Are you sure you want to ${isNew ? 'create' : 'update'} this slider?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: BUTTON_SAVE_COLOR,
          cancelButtonColor: BUTTON_CANCEL_COLOR
        });
      
        if (!confirm.isConfirmed) return;
      
        setIsSubmitting(true);
        try {
          const formDataToSend = new FormData();
          formDataToSend.append('title', formData.title);
          formDataToSend.append('subtitle', formData.subtitle || '');
          formDataToSend.append('altText', formData.alt);
          formDataToSend.append('isActive', formData.isActive);
          
          if (formData.image) {
            formDataToSend.append('image', formData.image);
          }
      
          if (isNew) {
            await sliderApi.create(formDataToSend);
          } else {
            await sliderApi.update(id, formDataToSend);
          }
      
          Swal.fire('Success', `Slider ${isNew ? 'created' : 'updated'}`, 'success');
          navigate('/admin/sliders');
        } catch (error) {
          console.error('Error:', error);
          Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
          setIsSubmitting(false);
        }
      };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/sliders')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="#" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Master Management
                            </Link>
                            <Link href="/admin/sliders" underline="hover" color="inherit">
                                Sliders
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>
                
                <Divider />
                
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Slider Information</Typography>
                            
                            <TextField
                                required
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            
                            <TextField
                                fullWidth
                                label="Subtitle"
                                name="subtitle"
                                value={formData.subtitle}
                                onChange={handleInputChange}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            
                            <TextField
                                required
                                fullWidth
                                label="Alt Text"
                                name="alt"
                                value={formData.alt}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.alt && Boolean(errors.alt)}
                                helperText={touched.alt && errors.alt}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            
                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={(e) => 
                                    setFormData(prev => ({ ...prev, isActive: e.target.checked }))
                                } />}
                                label="Active Slider"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Image Upload</Typography>
                            
                            <Box sx={{ 
                                p: 2, 
                                mb: 3, 
                                border: '1px solid',
                                borderColor: errors.image && touched.image ? 'error.main' : 'divider',
                                borderRadius: 1
                            }}>
                                <ImageUploader 
                                    onFileSelect={handleImageSelect}
                                    accept="image/*"
                                    maxSize={5 * 1024 * 1024}
                                />
                                {errors.image && touched.image && (
                                    <FormHelperText error>{errors.image}</FormHelperText>
                                )}
                                <Typography variant="caption" color="textSecondary">
                                    JPEG, PNG, GIF. Max 5MB.
                                </Typography>
                            </Box>

                            <Typography variant="h5" gutterBottom>Preview</Typography>
                            
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid', 
                                borderColor: 'divider',
                                borderRadius: 1,
                                minHeight: 200,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                {previewUrl || existingImageUrl ? (
                                    <Box sx={{ 
                                        position: 'relative', 
                                        width: '100%', 
                                        height: '100%',
                                        maxWidth: 300,
                                        maxHeight: 200
                                    }}>
                                        <img
                                            src={previewUrl || existingImageUrl}
                                            alt="Slider Preview"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                borderRadius: 4
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                opacity: 0,
                                                transition: 'opacity 0.3s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                '&:hover': { opacity: 1 }
                                            }}
                                            onClick={() => window.open(previewUrl || existingImageUrl, '_blank')}
                                        >
                                            <Typography variant="caption" color="white">
                                                View
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box textAlign="center">
                                        <InsertPhoto color="disabled" sx={{ fontSize: 48 }} />
                                        <Typography>No image selected</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <Divider />
                
                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/sliders')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SliderImageBody;